import { motion } from "framer-motion";
import { FileText, Download, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import CTABanner from "@/components/CTABanner";
import SEO from "@/components/SEO";

export default function SampleReports() {
  const reports = [
    {
      title: "Building Status Report",
      description: "Visual and digital check for approval related matters. See what's included in our building status reports.",
      size: "Sample PDF",
      pages: "Multi-page report",
      url: "/assets/reports/sample-building-status-report.pdf",
      features: [
        "Property details and scope",
        "Visual inspection findings",
        "Photographic documentation",
        "Clear recommendations"
      ]
    },
    {
      title: "Building Inspection & Condition Report",
      description: "Comprehensive visual assessment of building condition and aesthetics. Our most detailed inspection report.",
      size: "Sample PDF",
      pages: "Multi-page report",
      url: "/assets/reports/sample-building-inspection-and-condition-report.pdf",
      features: [
        "Detailed condition assessment",
        "Component by component analysis",
        "Extensive photo documentation",
        "Maintenance recommendations"
      ]
    },
    {
      title: "Remediation Report",
      description: "Cost estimates and recommendations for addressing identified issues. Included with all Building Condition Reports.",
      size: "Sample PDF",
      pages: "Multi-page report",
      url: "/assets/reports/sample-remediation-report.pdf",
      features: [
        "Issue prioritization",
        "Cost estimates for repairs",
        "Recommended actions",
        "Contractor guidance"
      ]
    },
    {
      title: "Visual Plumbing Report",
      description: "Full visual plumbing inspection covering water supply, drainage, and fixtures throughout the property.",
      size: "Sample PDF",
      pages: "Multi-page report",
      url: "/assets/reports/sample-visual-plumbing-report.pdf",
      features: [
        "Water supply systems",
        "Drainage assessment",
        "Fixture condition",
        "Visible defects documented"
      ]
    }
  ];

  const whatToExpect = [
    {
      title: "Clear Documentation",
      description: "Every report includes high resolution photos showing identified issues with detailed descriptions."
    },
    {
      title: "Easy to Understand",
      description: "Reports are written in plain English without unnecessary jargon. You'll know exactly what we found."
    },
    {
      title: "Digital Delivery",
      description: "Reports are delivered via email in PDF format within 72 hours of inspection."
    },
    {
      title: "Actionable Information",
      description: "Each finding includes context about severity and recommended next steps."
    }
  ];

  return (
    <div>
      <SEO 
        title="Sample Inspection Reports"
        description="View sample building inspection reports from Aussie Inspections Darwin. See what's included in our building status, condition, remediation and plumbing reports."
        keywords="sample building inspection report Darwin, inspection report example NT, building report format, property inspection report"
        url="/sample-reports"
      />

      <CTABanner />

      {/* Hero */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="font-chivo text-4xl md:text-5xl font-black text-slate-900 mb-6">
              Sample Inspection Reports
            </h1>
            <p className="text-xl text-slate-600">
              See what you'll receive. View sample reports to understand our inspection process and reporting format.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Reports Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {reports.map((report, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 border border-slate-200 rounded-lg p-8 hover:border-primary/50 hover:shadow-lg transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <FileText className="w-12 h-12 text-primary" />
                  <div className="text-right text-sm text-slate-500">
                    <p>{report.size}</p>
                    <p>{report.pages}</p>
                  </div>
                </div>

                <h3 className="font-chivo text-2xl font-bold text-slate-900 mb-3">
                  {report.title}
                </h3>
                <p className="text-slate-600 mb-6">
                  {report.description}
                </p>

                <ul className="space-y-2 mb-6">
                  {report.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2 text-sm text-slate-600">
                      <Eye className="w-4 h-4 text-secondary flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  asChild
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold"
                  data-testid={`view-report-${index}`}
                >
                  <a
                    href={report.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    View Sample Report
                  </a>
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-chivo text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              What to Expect in Your Report
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Every Aussie Inspections report is designed to be clear, comprehensive, and actionable
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whatToExpect.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-chivo text-lg font-bold text-slate-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Report Standards */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-slate-50 p-8 rounded-lg border border-slate-200"
          >
            <h2 className="font-chivo text-2xl font-bold text-slate-900 mb-4">
              Reporting Standards
            </h2>
            <div className="space-y-4 text-slate-600">
              <p>
                Our reports are aligned to AS 4349.1 where applicable and focus on documenting visible issues only. We do not provide structural opinions or make assumptions about hidden conditions.
              </p>
              <p>
                Each report clearly states the scope of inspection, limitations, and methodology. This ensures you understand exactly what was inspected and what was not accessible during the inspection.
              </p>
              <p>
                All findings are categorized and photographed. You'll receive a digital PDF report delivered via email within 72 hours of your inspection. Reports are yours to keep and share with relevant parties such as conveyancers, builders, or real estate agents.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-chivo text-4xl font-bold mb-6">
              Ready to Book Your Inspection?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Get your own detailed inspection report from Darwin's trusted inspectors
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-secondary hover:bg-secondary/90 text-white font-bold tracking-wide uppercase px-12"
              >
                <a href="/contact">Book Now</a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-primary font-bold tracking-wide uppercase px-12"
              >
                <a href="tel:+61413245519">Call 0413 24 55 19</a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
